// SipClient.jsx (FULL) — smaller widget + foldable CallScreen + SIP hold (music on hold) + call sounds (ringback + connect beep)
import React, { useEffect, useRef, useState } from "react";
import {
  UserAgent,
  Registerer,
  RegistererState,
  Inviter,
  SessionState,
  UserAgentState,
} from "sip.js";
import PhoneIcon from "@mui/icons-material/Phone";
import CallIcon from "@mui/icons-material/Call";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { sipClientStyles as s } from "../../helpers/style";
import { sipClient as sipTr } from "../../translate";
import CallScreen from "./CallScreen"; // adjust path
import beep from "../../assets/beep.mp3";
import ringtone from "../../assets/rington.mp3";
export default function SipClient({
  sip,
  defaultTarget = "",
  autoRegister = true,
  onRegistered,
  onIncoming,
  onCallState,
  onError,
  controlledOpen,
  onRequestClose,
}) {
  // Remote media output (must stay mounted even if UI hides)
  const audioRef = useRef(null);

  const uaRef = useRef(null);
  const registererRef = useRef(null);
  const sessionRef = useRef(null);

  const [registered, setRegistered] = useState(false);
  const [incoming, setIncoming] = useState(false);
  const [callState, setCallState] = useState("idle");
  const [target, setTarget] = useState(defaultTarget);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const effectiveOpen = isControlled ? controlledOpen : isOpen;

  // Call screen overlay
  const [callUiOpen, setCallUiOpen] = useState(false);
  const [remoteNumber, setRemoteNumber] = useState("");

  // Timer
  const [callSeconds, setCallSeconds] = useState(0);
  const timerRef = useRef(null);

  // Mute / Hold states
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const langIndex = +localStorage.getItem("atsLang") || 1;

  // ---- Call sounds (local) ----
  const ringbackRef = useRef(null);
  const ringbackIntervalRef = useRef(null);
  const connectBeepRef = useRef(null);
  const ringtoneRef = useRef(null);

  const startRingbackBeep = () => {
    try {
      if (ringbackIntervalRef.current) return;
      safePlay(ringbackRef.current);
      ringbackIntervalRef.current = setInterval(() => safePlay(ringbackRef.current), 3000);
    } catch {}
  };

  const stopRingbackBeep = () => {
    try {
      if (ringbackIntervalRef.current) {
        clearInterval(ringbackIntervalRef.current);
        ringbackIntervalRef.current = null;
      }
      safeStop(ringbackRef.current);
    } catch {}
  };

  const safePlay = async (el) => {
    try {
      if (!el) return;
      el.currentTime = 0;
      await el.play();
    } catch {}
  };

  const safeStop = (el) => {
    try {
      if (!el) return;
      el.pause();
      el.currentTime = 0;
    } catch {}
  };
  // -----------------------------

  const pushLog = (event, details) => {
    const time = new Date().toLocaleTimeString();
    const line =
      details === undefined
        ? `[${time}] ${event}`
        : `[${time}] ${event} — ${
            typeof details === "string" ? details : JSON.stringify(details)
          }`;
    console.log(line);
  };

  const statusColor = registered ? "#22c55e" : "#ef4444";
  const isEstablished = callState === "Established";

  const emitError = (e, context = "Error") => {
    const msg = e?.message || String(e);
    setError(msg);
    onError?.(msg);
    pushLog(context, msg);
  };

  const setState = (s) => {
    setCallState(s);
    onCallState?.(s);
    pushLog("Call state", s);
  };

  const openCallUi = () => setCallUiOpen(true);
  const closeCallUi = () => setCallUiOpen(false);

  // Keep remote audio playing even if UI is closed/folded
  const ensureAudioPlays = async () => {
    try {
      const el = audioRef.current;
      if (!el) return;
      el.muted = false;
      await el.play();
    } catch {}
  };

  // ✅ allow digits, optional leading +, and * #
  const sanitizePhoneInput = (value) => {
    if (!value) return "";
    let v = String(value).replace(/[^\d+*#]/g, "");
    if (v.includes("+")) {
      v = (v.startsWith("+") ? "+" : "") + v.replace(/\+/g, "");
    }
    return v;
  };

  const normalizeNumberToTargetUri = (input) => {
    const cleaned = sanitizePhoneInput((input || "").trim());
    if (!cleaned) throw new Error("Enter number to call");
    if (!sip?.domain) throw new Error("Missing sip.domain");

    // IMPORTANT: encode # and + in SIP URI user-part
    const userPart = encodeURIComponent(cleaned);
    const uriString = `sip:${userPart}@${sip.domain}`;
    const uri = UserAgent.makeURI(uriString);
    if (!uri) throw new Error(`Invalid target URI generated: ${uriString}`);
    return uri;
  };

  const streamAdd = (stream, track) => {
    try {
      if (track) stream.addTrack(track);
    } catch {}
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetTimer = () => {
    stopTimer();
    setCallSeconds(0);
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => setCallSeconds((s) => s + 1), 1000);
  };

  const getPeerConnection = () => {
    const s = sessionRef.current;
    return s?.sessionDescriptionHandler?.peerConnection || null;
  };

  // DTMF
  const getDtmfSender = (session) => {
    try {
      const pc = session?.sessionDescriptionHandler?.peerConnection;
      if (!pc) return null;
      const sender = pc
        .getSenders()
        .find((x) => x.track?.kind === "audio" && x.dtmf);
      return sender?.dtmf || null;
    } catch {
      return null;
    }
  };

  const sendDtmf = (tone) => {
    const s = sessionRef.current;
    if (!s) return;
    const dtmf = getDtmfSender(s);
    if (!dtmf) return;
    try {
      dtmf.insertDTMF(String(tone), 150, 80);
      pushLog("DTMF", `Sent ${tone}`);
    } catch (e) {
      pushLog("DTMF", `Error ${e?.message || e}`);
    }
  };

  // Mute (local mic)
  const toggleMute = () => {
    try {
      const pc = getPeerConnection();
      if (!pc) return;
      const sender = pc.getSenders().find((x) => x.track?.kind === "audio");
      if (!sender?.track) return;

      const nextEnabled = !sender.track.enabled;
      sender.track.enabled = nextEnabled; // enabled=true means NOT muted
      setMuted(!nextEnabled);
      pushLog("Mute", !nextEnabled ? "ON" : "OFF");
    } catch (e) {
      emitError(e, "Mute error");
    }
  };

  // SIP hold (re-INVITE) — allows PBX to play music on hold to the caller
  const toggleHold = async () => {
    try {
      const s = sessionRef.current;
      if (!s || !isEstablished) return;

      const nextHeld = !held;

      const sdhOpts = s.sessionDescriptionHandlerOptionsReInvite || {};
      sdhOpts.hold = nextHeld;
      s.sessionDescriptionHandlerOptionsReInvite = { ...sdhOpts };

      const options = {
        requestDelegate: {
          onAccept: () => {
            setHeld(nextHeld);
            const pc = getPeerConnection();
            if (pc) {
              const audioSender = pc.getSenders().find((x) => x.track?.kind === "audio");
              if (audioSender?.track) audioSender.track.enabled = !nextHeld;
            }
            pushLog("Hold", nextHeld ? "ON (SIP)" : "OFF (SIP)");
          },
          onReject: () => {
            pushLog("Hold", "re-INVITE rejected");
            emitError(new Error("Hold request was rejected"), "Hold");
          },
        },
      };

      await s.invite(options);
      // Update UI optimistically; onAccept will also fire
      setHeld(nextHeld);
      const pc = getPeerConnection();
      if (pc) {
        const audioSender = pc.getSenders().find((x) => x.track?.kind === "audio");
        if (audioSender?.track) audioSender.track.enabled = !nextHeld;
      }
    } catch (e) {
      pushLog("Hold", `Error: ${e?.message || e}`);
      emitError(e, "Hold error");
    }
  };

  // Transfer (REFER)
  const transferCall = async (to) => {
    try {
      const s = sessionRef.current;
      if (!s) throw new Error("No active session");
      if (!isEstablished) throw new Error("Transfer only after Established");
      if (typeof s.refer !== "function") {
        throw new Error("Session has no refer() method. Check SIP.js version.");
      }

      const targetUri = normalizeNumberToTargetUri(to);
      await s.refer(targetUri);
      pushLog("Transfer", `REFER -> ${to}`);
    } catch (e) {
      emitError(e, "Transfer error");
    }
  };

  const wireSessionAudio = (session) => {
    session.stateChange.addListener((state) => {
      const name = SessionState[state] || String(state);
      setState(name);

      if (state === SessionState.Establishing) {
        startRingbackBeep();
      }

      if (state === SessionState.Established) {
        stopRingbackBeep();
        resetTimer();
        startTimer();
        openCallUi();
        setMuted(false);
        setHeld(false);

        const pc = session.sessionDescriptionHandler?.peerConnection;
        if (!pc) return;

        const remoteStream = new MediaStream();
        pc.getReceivers().forEach((r) => r.track && streamAdd(remoteStream, r.track));

        if (audioRef.current) {
          audioRef.current.srcObject = remoteStream;
          ensureAudioPlays();
        }
      }

      if (state === SessionState.Terminated) {
        stopRingbackBeep();
        safeStop(ringtoneRef.current);
        stopTimer();
        setIncoming(false);
        sessionRef.current = null;
        setCallState("idle");
        setMuted(false);
        setHeld(false);
        closeCallUi();
      }
    });
  };

  const start = async () => {
    const port = sip?.port ?? sip?.sip_port ?? 8089;
    const wssServer =
      sip?.wssServer || (sip?.domain ? `wss://${sip.domain}:${port}/ws` : null);
    if (!wssServer || !sip?.domain || !sip?.user || !sip?.password) {
      throw new Error("Missing SIP props: wssServer (or domain), user, password are required");
    }

    // App.js style: port in URI (sip:user@domain:8089)
    const regUri = UserAgent.makeURI(`sip:${sip.user}@${sip.domain}:${port}`);
    if (!regUri) throw new Error(`Invalid registration SIP URI: sip:${sip.user}@${sip.domain}`);

    // App.js registration logic: Asterisk options + connectionTimeout, iceServers, noAnswerTimeout
    const ua = new UserAgent({
      uri: regUri,
      transportOptions: {
        server: wssServer,
        traceSip: false,
        connectionTimeout: sip.connectionTimeout ?? 3600,
      },
      contactName: sip.contactName ?? sip.user,
      displayName: sip.displayName ?? sip.user,
      authorizationUsername: sip.user,
      authorizationPassword: sip.password,
      userAgentString: sip.userAgentString ?? sip.user,
      noAnswerTimeout: sip.noAnswerTimeout ?? 120,
      contactParams: { transport: "wss" },
      hackIpInContact: true,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          bundlePolicy: "balanced",
          iceServers: sip.iceServers ?? [{ urls: "stun:stun.l.google.com:19302" }],
        },
        iceGatheringTimeout: sip.iceGatheringTimeout ?? 3600,
        constraints: { audio: true, video: false },
      },
    });

    // App.js: ReconnectionAttempts on transport (sip.js 0.21 may support via transportOptions)
    if (ua.transport && typeof ua.transport.ReconnectionAttempts !== "undefined") {
      ua.transport.ReconnectionAttempts = sip.reconnectionAttempts ?? 300;
    }
    const registeringRef = { current: false };
    const isReRegisterRef = { current: false };

    const registerer = new Registerer(ua, {
      expires: sip.registerExpires ?? 3000,
      extraHeaders: sip.registerExtraHeaders ?? [],
      extraContactHeaderParams: sip.registerExtraContactParams ?? [],
    });

    registerer.stateChange.addListener((newState) => {
      pushLog("RegistererState", RegistererState[newState] ?? newState);
      switch (newState) {
        case RegistererState.Registered:
          if (!isReRegisterRef.current) {
            pushLog("Register", "Registered");
            setRegistered(true);
            onRegistered?.(true);
          } else {
            pushLog("Register", "ReRegistered");
          }
          registeringRef.current = false;
          isReRegisterRef.current = true;
          break;
        case RegistererState.Unregistered:
          setRegistered(false);
          onRegistered?.(false);
          break;
        default:
          break;
      }
    });

    ua.delegate = {
      onConnect: () => {
        pushLog("Transport", "Connected");
        if (ua.transport && typeof ua.transport.ReconnectionAttempts !== "undefined") {
          ua.transport.ReconnectionAttempts = sip.reconnectionAttempts ?? 999;
        }
        // App.js: register only if not attemptingReconnection and not already registering
        if (!registeringRef.current) {
          window.setTimeout(() => {
            if (!uaRef.current || ua !== uaRef.current) return;
            if (registeringRef.current) return;
            pushLog("Register", "Sending REGISTER...");
            registeringRef.current = true;
            const registerOptions = {
              requestDelegate: {
                onReject: (rej) => {
                  pushLog("Register reject", rej?.message?.reasonPhrase ?? rej);
                  registeringRef.current = false;
                  setRegistered(false);
                  onRegistered?.(false);
                },
              },
            };
            registerer.register(registerOptions).catch((e) => {
              pushLog("Register error", e?.message || e);
              registeringRef.current = false;
              setRegistered(false);
              onRegistered?.(false);
            });
          }, 500);
        }
      },
      onDisconnect: (err) => {
        pushLog("Transport", err ? `Disconnected: ${err?.message || err}` : "Disconnected");
        isReRegisterRef.current = false;
        setRegistered(false);
        onRegistered?.(false);
      },
      onInvite: (invitation) => {
        pushLog("Incoming", "INVITE received ✅");

        stopRingbackBeep();
        safePlay(ringtoneRef.current);

        // close the main dial-pad widget when incoming call arrives
        if (isControlled) onRequestClose?.();
        else setIsOpen(false);

        const fromUser = invitation?.remoteIdentity?.uri?.user;
        const fromUri = invitation?.remoteIdentity?.uri?.toString?.() || "";
        setRemoteNumber(fromUser || fromUri || "Incoming");

        sessionRef.current = invitation;
        wireSessionAudio(invitation);

        resetTimer();
        setIncoming(true);
        setState("Incoming");
        openCallUi();
        onIncoming?.();
      },
    };

    uaRef.current = ua;
    registererRef.current = registerer;

    await ua.start();
    setCallState("idle");
  };

  const stop = async () => {
    stopRingbackBeep();
    stopTimer();

    try {
      if (registererRef.current) await registererRef.current.unregister();
    } catch {}

    try {
      if (uaRef.current?.state === UserAgentState.Started) {
        await uaRef.current.stop();
      }
    } catch {}

    uaRef.current = null;
    registererRef.current = null;
    sessionRef.current = null;

    setRegistered(false);
    onRegistered?.(false);
    setIncoming(false);
    setCallState("idle");
    resetTimer();
    setMuted(false);
    setHeld(false);
    closeCallUi();
  };

  // cleanup
  useEffect(() => {
    return () => {
      stop().catch(() => {});
    };
  }, []);

  // start/restart when sip props change
  const lastKeyRef = useRef("");
  useEffect(() => {
    if (!autoRegister) return;
    const wss = sip?.wssServer || (sip?.domain ? `wss://${sip.domain}:8089/ws` : null);
    if (!wss || !sip?.domain || !sip?.user || !sip?.password) return;

    const key = `${wss}|${sip?.domain}|${sip?.user}|${sip?.password}`;

    if (!lastKeyRef.current) {
      lastKeyRef.current = key;
      start().catch((e) => emitError(e, "Start error"));
      return;
    }

    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      (async () => {
        try {
          await stop();
          await start();
        } catch (e) {
          emitError(e, "Restart error");
        }
      })();
    }
  }, [autoRegister, sip?.wssServer, sip?.domain, sip?.user, sip?.password]);

  const call = async () => {
    try {
      setError("");
      resetTimer();

      if (!registered) throw new Error("Not registered yet");
      const ua = uaRef.current;
      if (!ua) throw new Error("UA not ready");

      const targetUri = normalizeNumberToTargetUri(target);

      const inviter = new Inviter(ua, targetUri, {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });

      sessionRef.current = inviter;
      wireSessionAudio(inviter);

      setIncoming(false);
      setRemoteNumber(target);
      setState("Calling");
      openCallUi();

      if (isControlled) onRequestClose?.();
      else setIsOpen(false);

      const inviteOptions = {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
        extraHeaders: Array.isArray(sip?.inviteExtraHeaders) ? sip.inviteExtraHeaders : [],
      };
      await inviter.invite(inviteOptions);
    } catch (e) {
      emitError(e, "Call error");
      stopRingbackBeep();
    }
  };

  const hangup = async () => {
    try {
      setError("");
      stopRingbackBeep();
      safeStop(ringtoneRef.current);

      const s = sessionRef.current;
      if (!s) return;

      if (s.state === SessionState.Established) await s.bye();
      else {
        if (s.cancel) await s.cancel();
        else if (s.reject) await s.reject();
      }

      sessionRef.current = null;
      setIncoming(false);
      setCallState("idle");
      resetTimer();
      setMuted(false);
      setHeld(false);
      closeCallUi();
    } catch (e) {
      emitError(e, "Hangup error");
    }
  };

  const accept = async () => {
    try {
      setError("");
      stopRingbackBeep();
      safeStop(ringtoneRef.current);

      const s = sessionRef.current;
      if (!s) return;

      await s.accept({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });

      setIncoming(false);
      openCallUi();
    } catch (e) {
      emitError(e, "Accept error");
    }
  };

  const reject = async () => {
    try {
      setError("");
      stopRingbackBeep();
      safeStop(ringtoneRef.current);

      const s = sessionRef.current;
      if (!s) return;

      await s.reject();

      sessionRef.current = null;
      setIncoming(false);
      setCallState("idle");
      resetTimer();
      setMuted(false);
      setHeld(false);
      closeCallUi();
    } catch (e) {
      emitError(e, "Reject error");
    }
  };

  // ---------------- Dial pad + long press 0 -> + ----------------
  const dialPad = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const addSymbolToTarget = (sym) => {
    if (isEstablished) {
      if (sym !== "+") sendDtmf(sym);
      return;
    }

    if (sym === "+") {
      setTarget((prev) => {
        const p = sanitizePhoneInput(prev || "");
        if (p.startsWith("+")) return p;
        if (p.length === 0) return "+";
        return p;
      });
      return;
    }

    setTarget((prev) => sanitizePhoneInput((prev || "") + sym));
  };

  const onPadDown = (sym) => {
    longPressTriggeredRef.current = false;
    clearLongPress();
    if (sym !== "0") return;

    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      addSymbolToTarget("+");
    }, 450);
  };

  const onPadUp = (sym) => {
    clearLongPress();
    if (sym === "0" && longPressTriggeredRef.current) return;
    addSymbolToTarget(sym);
  };

  const backspace = () => setTarget((prev) => (prev || "").slice(0, -1));

  const mainCallButtonAction = async () => {
    if (incoming || callState === "Calling" || callState === "Established") {
      openCallUi();
      return;
    }
    await call();
  };

  const canTransfer = typeof sessionRef.current?.refer === "function";

  const handleReconnect = async () => {
    if (reconnecting || registered) return;
    setReconnecting(true);
    setError("");
    try {
      await stop();
      await start();
    } catch (e) {
      emitError(e, "Reconnect error");
    } finally {
      setReconnecting(false);
    }
  };

  return (
    <>
      {/* ✅ ALWAYS mounted remote audio */}
      <audio ref={audioRef} autoPlay playsInline style={s.audioHidden} />

      {/* Call sounds (local) */}
      <audio ref={ringbackRef} preload="auto" src={beep} style={s.audioHidden} />
      <audio ref={connectBeepRef} preload="auto" src="/sounds/connect-beep.mp3" style={s.audioHidden} />
      <audio ref={ringtoneRef} preload="auto" loop src={ringtone} style={s.audioHidden} />

      {!effectiveOpen && !isControlled ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          title="Open phone"
          style={s.toggleButton(statusColor)}
        >
          <PhoneIcon style={{ fontSize: 26 }} />
        </button>
      ) : effectiveOpen ? (
        <div style={s.panel}>
          {/* Header: Phone + Register */}
          <div style={s.header}>
            <span style={s.headerTitleRow}>
              <HeadsetMicIcon sx={{ fontSize: 22, color: '#1e293b' }} />
              <span style={{ ...s.headerTitle, fontSize: 14, fontWeight: 600 }}>
                {registered ? (sipTr.online?.[langIndex] ?? "Online") : (sipTr.offline?.[langIndex] ?? "Offline")}
              </span>
              <span style={s.headerRegDot(registered)} title={registered ? "Registered" : "Not registered"} />
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {!registered && (
                <button
                  type="button"
                  onClick={handleReconnect}
                  disabled={reconnecting}
                  style={{
                    padding: "6px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: "1px solid rgba(34,197,94,0.4)",
                    background: "rgba(34,197,94,0.1)",
                    color: "#16a34a",
                    cursor: reconnecting ? "not-allowed" : "pointer",
                    opacity: reconnecting ? 0.7 : 1,
                  }}
                  title={sipTr.reconnect?.[langIndex] ?? "Reconnect"}
                >
                  {reconnecting ? "…" : (sipTr.reconnect?.[langIndex] ?? "Reconnect")}
                </button>
              )}
              <button
                type="button"
                onClick={() => (isControlled ? onRequestClose?.() : setIsOpen(false))}
                style={s.closeBtn}
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {error && (
            <div style={s.errorBox}>
              <b>Error:</b> {error}
            </div>
          )}

          {/* Input */}
          <input
            value={target}
            onChange={(e) => setTarget(sanitizePhoneInput(e.target.value))}
            placeholder={sipTr.number?.[langIndex] ?? "Number"}
            style={s.numberInput}
          />

          {/* Dial pad */}
          <div>
            <div style={s.dialPadGrid}>
              {dialPad.map((d) => (
                <button
                  key={d}
                  type="button"
                  onMouseDown={() => onPadDown(d)}
                  onMouseUp={() => onPadUp(d)}
                  onMouseLeave={() => clearLongPress()}
                  onTouchStart={() => onPadDown(d)}
                  onTouchEnd={() => onPadUp(d)}
                  style={s.padBtn}
                  title={
                    d === "0"
                      ? isEstablished
                        ? ""
                        : "Tap: 0 • Hold: +"
                      : isEstablished
                      ? ``
                      : `Add ${d}`
                  }
                >
                  {d === "0" ? (
                    <span style={s.padZeroPlus}>
                      <span>{d}</span>
                      <span style={s.padZeroPlusLabel}>+</span>
                    </span>
                  ) : (
                    d
                  )}
                </button>
              ))}
            </div>

            <div style={s.actionRow}>
              <button type="button" onClick={backspace} style={s.backspaceBtn}>
                ⌫
              </button>
              <button type="button" onClick={() => setTarget("")} style={s.clearBtn}>
                Clear
              </button>
            </div>
          </div>

          {/* Call button */}
          <div style={s.callBtnWrap}>
            <button
              type="button"
              onClick={mainCallButtonAction}
              disabled={!registered || !target.trim()}
              style={s.callBtn(!!(registered && target.trim()))}
              title="Call"
            >
              <CallIcon sx={{ fontSize: 24 }} />
            </button>
          </div>
        </div>
      ) : null}

      {/* Call screen overlay */}
      <CallScreen
        open={callUiOpen}
        onClose={closeCallUi}
        callState={callState}
        number={callState === "Incoming" ? remoteNumber : remoteNumber || target}
        seconds={callSeconds}
        onAccept={accept}
        onReject={reject}
        onHangup={hangup}
        onSendDtmf={sendDtmf}
        muted={muted}
        held={held}
        onToggleMute={toggleMute}
        onToggleHold={toggleHold}
        onTransfer={transferCall}
        canTransfer={canTransfer}
      />
    </>
  );
}